'use client';

import { Artista } from "@/types/artista";
import { useEffect, useState } from "react";
import { getYouTubeChannelData, type YouTubeChannelData } from "@/services/social.service";

// ─── Parse helpers ───────────────────────────────────────────────────────────

function parseSpotifyEmbed(url: string): string | null {
    const match = url.match(/open\.spotify\.com\/(artist|album|track)\/([a-zA-Z0-9]+)/);
    if (match) return `https://open.spotify.com/embed//${match[1]}/${match[2]}`;
    return null;
}

function parseYouTubeEmbed(url: string): string | null {
    let match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/);
    if (match) return `https://www.youtube.com/embed/${match[1]}`;
    match = url.match(/youtube\.com\/playlist\?list=([a-zA-Z0-9_-]+)/);
    if (match) return `https://www.youtube.com/embed/videoseries?list=${match[1]}`;
    return null;
}

function parseYouTubeChannel(url: string): { handle?: string; channelId?: string } | null {
    let match = url.match(/youtube\.com\/@([a-zA-Z0-9_.-]+)/);
    if (match) return { handle: match[1] };
    match = url.match(/youtube\.com\/channel\/(UC[a-zA-Z0-9_-]+)/);
    if (match) return { channelId: match[1] };
    return null;
}

function parseInstagramEmbed(url: string): string | null {
    const match = url.match(/instagram\.com\/(p|reel|tv)\/([a-zA-Z0-9_-]+)/);
    if (match) return `https://www.instagram.com/${match[1]}/${match[2]}/embed/`;
    return null;
}

function parseInstagramUsername(url: string): string | null {
    if (url.startsWith('@')) return url.slice(1);
    const match = url.match(/instagram\.com\/([a-zA-Z0-9_.]+)\/?$/);
    if (match && !['p', 'reel', 'tv', 'explore', 'stories'].includes(match[1])) return match[1];
    return null;
}

// ─── Shared skeleton ─────────────────────────────────────────────────────────

function EmbedSkeleton({ className }: { className?: string }) {
    return <div className={`rounded-xl bg-zinc-800 animate-pulse ${className ?? ''}`} />;
}

// ─── Icons ───────────────────────────────────────────────────────────────────

function YouTubeIcon({ className }: { className?: string }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="currentColor">
            <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
        </svg>
    );
}

function InstagramIcon({ className }: { className?: string }) {
    return (
        <svg className={className} fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
        </svg>
    );
}

// ─── Spotify embed ───────────────────────────────────────────────────────────

function SpotifyEmbed({ url }: { url: string }) {
    const [loaded, setLoaded] = useState(false);
    const embedUrl = parseSpotifyEmbed(url);
    if (!embedUrl) return null;
    return (
        <div key={embedUrl} className="relative h-[152px]">
            {!loaded && <EmbedSkeleton className="absolute inset-0" />}
            <iframe
                key={embedUrl}
                src={embedUrl}
                width="100%"
                height="152"
                allow="encrypted-media"
                sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
                className={`rounded-xl border-0 w-full h-full transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
                onLoad={() => setLoaded(true)}
            />
        </div>
    );
}

// ─── YouTube channel card ────────────────────────────────────────────────────


function formatSubscribers(count: string): string {
    const n = parseInt(count);
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M suscriptores`;
    if (n >= 1_000) return `${Math.floor(n / 1_000)}K suscriptores`;
    return `${count} suscriptores`;
}

function YouTubeChannelCard({ url }: { url: string }) {
    const [data, setData] = useState<YouTubeChannelData | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const channel = parseYouTubeChannel(url);
        if (!channel) { setLoading(false); return; }

        let mounted = true;

        getYouTubeChannelData(channel.handle, channel.channelId)
            .then(d => {
                if (mounted) {
                    setData(d);
                    setLoading(false);
                }
            })
            .catch(err => {
                console.error('[YouTubeChannelCard] fetch failed:', err);
                if (mounted) setLoading(false);
            });

        return () => {
            mounted = false;
        };
    }, [url]);

    const channelUrl = url.startsWith('http') ? url : `https://youtube.com/@${url}`;

    if (loading) {
        return (
            <div className="rounded-xl overflow-hidden animate-pulse">
                <div className="h-28 bg-zinc-800" />
                <div className="bg-zinc-900 flex gap-4 px-5 pb-4">
                    <div className="w-16 h-16 rounded-full bg-zinc-800 shrink-0 -mt-8 border-2 border-zinc-900" />
                    <div className="flex-1 space-y-2 pt-2">
                        <div className="h-4 bg-zinc-800 rounded w-2/5" />
                        <div className="h-3 bg-zinc-700 rounded w-1/3" />
                    </div>
                </div>
            </div>
        );
    }

    if (!data) {
        return (
            <a
                href={channelUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm text-red-400 hover:text-red-300 transition-colors"
            >
                <YouTubeIcon className="w-5 h-5" />
                Ver canal de YouTube
            </a>
        );
    }

    return (
        <div className="rounded-xl overflow-hidden bg-zinc-900 border border-zinc-800">
            <div className="h-28 bg-gradient-to-br from-red-950 to-zinc-900 relative overflow-hidden">
                {data.banner && (
                    <img src={data.banner} alt="" className="w-full h-full object-cover opacity-75" />
                )}
            </div>
            <div className="flex gap-4 px-5 pb-4">
                <div className="shrink-0 -mt-8">
                    {data.avatar
                        ? <img src={data.avatar} alt={data.title} className="w-16 h-16 rounded-full border-2 border-zinc-900 object-cover relative z-10" />
                        : <div className="w-16 h-16 rounded-full border-2 border-zinc-900 bg-red-600 flex items-center justify-center">
                            <YouTubeIcon className="w-8 h-8 text-white" />
                        </div>
                    }
                </div>
                <div className="flex flex-1 items-center justify-between gap-3 pt-2 min-w-0">
                    <div className="min-w-0">
                        <p className="font-semibold text-white truncate">{data.title}</p>
                        <p className="text-sm text-zinc-400 truncate">
                            {data.customUrl && `@${data.customUrl.replace('@', '')}`}
                            {data.customUrl && data.subscriberCount && ' · '}
                            {data.subscriberCount && formatSubscribers(data.subscriberCount)}
                        </p>
                    </div>
                    <a
                        href={channelUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="shrink-0 flex items-center gap-1.5 bg-red-600 hover:bg-red-500 text-white text-sm font-semibold px-4 py-2 rounded-full transition-colors"
                    >
                        <YouTubeIcon className="w-4 h-4" />
                        Ver canal
                    </a>
                </div>
            </div>
        </div>
    );
}

function YouTubeEmbed({ url }: { url: string }) {
    const [loaded, setLoaded] = useState(false);
    const embedUrl = parseYouTubeEmbed(url);
    if (!embedUrl) return null;
    return (
        <div key={embedUrl} className="relative aspect-video">
            {!loaded && <EmbedSkeleton className="absolute inset-0" />}
            <iframe
                key={embedUrl}
                src={embedUrl}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
                allowFullScreen
                className={`rounded-xl border-0 w-full h-full transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
                onLoad={() => setLoaded(true)}
            />
        </div>
    );
}

// ─── Instagram ───────────────────────────────────────────────────────────────

function InstagramProfileCard({ url }: { url: string }) {
    const username = parseInstagramUsername(url);
    const profileUrl = url.startsWith('http') ? url : `https://instagram.com/${username}`;

    return (
        <div className="rounded-xl overflow-hidden border border-zinc-800">
            <div
                className="flex items-center gap-4 px-5 py-5"
                style={{ background: 'linear-gradient(135deg, #833ab4 0%, #fd1d1d 50%, #fcb045 100%)' }}
            >
                <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center shrink-0">
                    <InstagramIcon className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-white font-semibold truncate">@{username}</p>
                    <p className="text-white/70 text-sm">Instagram</p>
                </div>
                <a
                    href={profileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="shrink-0 bg-white/20 hover:bg-white/30 text-white text-sm font-semibold px-4 py-2 rounded-full transition-colors backdrop-blur-sm"
                >
                    Ver perfil
                </a>
            </div>
        </div>
    );
}

function InstagramEmbed({ url }: { url: string }) {
    const [loaded, setLoaded] = useState(false);
    const embedUrl = parseInstagramEmbed(url);
    if (!embedUrl) return null;
    return (
        <div key={embedUrl} className="relative h-[480px]">
            {!loaded && <EmbedSkeleton className="absolute inset-0" />}
            <iframe
                key={embedUrl}
                src={embedUrl}
                sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
                className={`rounded-xl border-0 w-full h-full transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
                onLoad={() => setLoaded(true)}
            />
        </div>
    );
}

// ─── Main export ─────────────────────────────────────────────────────────────

export function SocialEmbeds({ artista }: { artista: Artista }) {
    if (!artista.spotifyUrl && !artista.youtubeUrl && !artista.instagram) return null;

    const youtubeIsChannel = artista.youtubeUrl ? !!parseYouTubeChannel(artista.youtubeUrl) : false;
    const instagramIsProfile = artista.instagram ? !parseInstagramEmbed(artista.instagram) : false;

    return (
        <div className="space-y-4 mt-6">
            {artista.spotifyUrl && <SpotifyEmbed url={artista.spotifyUrl} />}
            {artista.youtubeUrl && (
                youtubeIsChannel
                    ? <YouTubeChannelCard url={artista.youtubeUrl} />
                    : <YouTubeEmbed url={artista.youtubeUrl} />
            )}
            {artista.instagram && (
                instagramIsProfile
                    ? <InstagramProfileCard url={artista.instagram} />
                    : <InstagramEmbed url={artista.instagram} />
            )}
        </div>
    );
}

// ─── Split exports for separate placement ────────────────────────────────────

export function SpotifySection({ artista }: { artista: Artista }) {
    if (!artista.spotifyUrl) return null;
    return (
        <div className="mt-6">
            <SpotifyEmbed url={artista.spotifyUrl} />
        </div>
    );
}

export function InstagramYouTubeSection({ artista }: { artista: Artista }) {
    if (!artista.youtubeUrl && !artista.instagram) return null;

    const youtubeIsChannel = artista.youtubeUrl ? !!parseYouTubeChannel(artista.youtubeUrl) : false;
    const instagramIsProfile = artista.instagram ? !parseInstagramEmbed(artista.instagram) : false;

    return (
        <div className="space-y-6 mt-8">
            {artista.instagram && (
                instagramIsProfile
                    ? <InstagramProfileCard url={artista.instagram} />
                    : <InstagramEmbed url={artista.instagram} />
            )}
            {artista.youtubeUrl && (
                youtubeIsChannel
                    ? <YouTubeChannelCard url={artista.youtubeUrl} />
                    : <YouTubeEmbed url={artista.youtubeUrl} />
            )}
        </div>
    );
}
